import React, { Component } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import * as XLSX from "xlsx";
import HelperClass from "./../../../config/helperClass";
import {
  getClientInvoices,
} from "./../../../config/client_api_calls";
import { addSettings } from "../../../actions";
import MsaWarning from "../ExtraComponents/MsaWarning";
import SettingsError from "../ExtraComponents/SettingsError";
import ViewInvoiceModal from "../../../components/Invoices/ViewInvoiceModal";
import NotificationToast from "../../../components/Notification/NotificationToast";
import "./styles.css";

const auth = new HelperClass();

// ── Helpers ───────────────────────────────────────────────────────────────────

function isOverdue(row) {
  if (!row.due_date) return false;
  if (row.payment_status === "Paid") return false;
  return new Date(row.due_date) < new Date();
}

function fmtCompact(val) {
  const n = Number(val);
  if (!Number.isFinite(n)) return "$0";
  if (n >= 1000) return "$" + (n / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  return "$" + n.toFixed(0);
}

function fmtDate(val) {
  if (!val) return "—";
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return val;
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${mm}-${dd}-${d.getFullYear()}`;
  } catch {
    return val;
  }
}

function fmtCurrency(val, decimals) {
  const n = Number(val);
  if (!Number.isFinite(n)) return "$0.00";
  const fixed = n.toFixed(decimals || 2);
  const [intPart, decPart] = fixed.split(".");
  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return "$" + (decPart !== undefined ? withCommas + "." + decPart : withCommas);
}

function getInvoiceBalance(invoice) {
  if (invoice != null && invoice.balance_due != null) {
    const stored = Number(invoice.balance_due);
    if (Number.isFinite(stored)) return Math.max(0, stored);
  }
  return Math.max(
    0,
    (Number(invoice && invoice.grand_total) || 0) -
      (Number(invoice && invoice.credit_applied) || 0) -
      (Number(invoice && invoice.amount_paid) || 0)
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status, row }) {
  const overdue = isOverdue(row);
  if (status === "Paid") {
    return (
      <span className="inv-status-badge" style={{ background: "rgba(26,122,74,0.1)", color: "#1A7A4A" }}>
        Paid
      </span>
    );
  }
  if (status === "Partial") {
    return (
      <span className="inv-status-badge" style={{ background: "rgba(79,70,229,0.1)", color: "#4F46E5" }}>
        Partial
      </span>
    );
  }
  if (overdue) {
    return (
      <span className="inv-status-badge" style={{ background: "rgba(220,38,38,0.12)", color: "#DC2626" }}>
        Unpaid
      </span>
    );
  }
  return (
    <span className="inv-status-badge" style={{ background: "rgba(245,158,11,0.1)", color: "#D97706" }}>
      Unpaid
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

class Invoices extends Component {
  constructor(props) {
    super(props);
    console.log("[Invoices] constructor → props:", props);
    this._decimals = 2; // resolved in componentDidMount from quotationSettings
    this._searchTimer = null;

    this.state = {
      // ── toast ──
      toastMsg: "",
      toastType: "success",

      // ── list ──
      rows: [],
      total: 0,
      summary: { total_invoiced: 0, total_paid: 0, total_outstanding: 0, invoice_count: 0 },
      page: 1,
      limit: 10,
      search: "",
      statusFilter: "",
      orderFilter: "",
      dateFrom: "",
      dateTo: "",
      loading: false,
      errorMessage: "",
      filtersApplied: false,

      // ── view ──
      selectedInvoice: null,
      showViewModal: false,
    };
  }

  componentDidMount() {
    console.log("[Invoices] componentDidMount");
    console.log("[Invoices] location:", this.props.location);
    console.log("[Invoices] clientAccount:", auth.getClientAccount());
    console.log("[Invoices] clientToken:", auth.getClientToken());
    const qs = this.props.quotationSettings;
    if (qs && qs[0]) {
      const dp = qs[0].billing_decimal_point || qs[0].decimal_point;
      if (dp && dp !== "") this._decimals = Number(dp);
    }
    console.log("[Invoices] _decimals:", this._decimals);
    const params = new URLSearchParams(
      this.props.location && this.props.location.search ? this.props.location.search : ""
    );
    const orderno = params.get("orderno");
    if (orderno) {
      console.log("[Invoices] orderno from URL:", orderno);
      this.setState({ orderFilter: orderno }, this.fetchInvoices);
    } else {
      this.fetchInvoices();
    }
  }

  // ── Data fetching ─────────────────────────────────────────────────────────

  fetchInvoices = async () => {
    const { page, limit, search, statusFilter, orderFilter, dateFrom, dateTo } = this.state;
    console.log("[Invoices] fetchInvoices → params:", { page, limit, search, statusFilter, orderFilter, dateFrom, dateTo });
    this.setState({ loading: true, errorMessage: "" });
    try {
      const response = await getClientInvoices(
        auth.getClientAccount(), auth.getClientToken(),
        page, limit, search, statusFilter, dateFrom, dateTo, orderFilter
      );
      console.log("[Invoices] fetchInvoices → raw response:", response);
      const payload = response && response.data;
      console.log("[Invoices] fetchInvoices → payload:", payload);
      if (
        payload &&
        (payload.status === 403 ||
          payload.errors === "authentication missing" ||
          payload.errors === "jwt expired")
      ) {
        console.warn("[Invoices] auth error → logging out:", payload);
        auth.clientLogout();
        return;
      }
      if (payload && payload.status === 200 && payload.data) {
        console.log("[Invoices] fetchInvoices → success, rows:", payload.data.rows, "total:", payload.data.total);
        this.setState({
          rows: payload.data.rows || [],
          total: Number(payload.data.total) || 0,
          summary: payload.data.summary || this.state.summary,
          loading: false,
        });
      } else {
        const msg = (payload && payload.message) || "Failed to load invoices.";
        console.error("[Invoices] fetchInvoices → unexpected payload:", payload, "msg:", msg);
        this.setState({ loading: false, errorMessage: msg, toastMsg: msg, toastType: "error" });
      }
    } catch (err) {
      console.error("[Invoices] fetchInvoices → caught error:", err);
      this.setState({
        loading: false,
        errorMessage: "Failed to load invoices.",
        toastMsg: "Failed to load invoices.",
        toastType: "error",
      });
    }
  };

  openDetail = (row) => {
    console.log("[Invoices] openDetail → row:", row);
    console.log("[Invoices] invoiceNo passed to modal:", row && row.invoice_no);
    this.setState({ showViewModal: true, selectedInvoice: row });
  };

  closeViewModal = () => {
    this.setState({ showViewModal: false, selectedInvoice: null });
  };

  // ── Pagination / filters ──────────────────────────────────────────────────

  onPageChange   = (page)  => this.setState({ page },            this.fetchInvoices);
  onLimitChange  = (limit) => this.setState({ limit, page: 1 }, this.fetchInvoices);
  onSearchChange = (e) => {
    const search = e.target.value;
    this.setState({ search, page: 1, filtersApplied: true });
    clearTimeout(this._searchTimer);
    this._searchTimer = setTimeout(this.fetchInvoices, 400);
  };
  onStatusFilter = (e) => {
    const statusFilter = e.target.value;
    const { search, orderFilter, dateFrom, dateTo } = this.state;
    const hasOtherFilters = search || orderFilter || dateFrom || dateTo;
    this.setState({ statusFilter, page: 1, filtersApplied: statusFilter !== "" || hasOtherFilters }, this.fetchInvoices);
  };
  onOrderFilter  = (e) => this.setState({ orderFilter: e.target.value,  page: 1, filtersApplied: true }, this.fetchInvoices);
  onDateFrom     = (e) => this.setState({ dateFrom: e.target.value,     page: 1, filtersApplied: true }, this.fetchInvoices);
  onDateTo       = (e) => this.setState({ dateTo: e.target.value,       page: 1, filtersApplied: true }, this.fetchInvoices);

  clearOrderFilter = () => {
    const params = new URLSearchParams(
      this.props.location && this.props.location.search ? this.props.location.search : ""
    );
    if (params.has("orderno")) {
      params.delete("orderno");
      const newSearch = params.toString();
      this.props.history && this.props.history.replace({ search: newSearch ? `?${newSearch}` : "" });
    }
    this.setState({ orderFilter: "" }, this.fetchInvoices);
  };


  exportToExcel = () => {
    const { rows } = this.state;
    if (!rows || rows.length === 0) return;

    const data = rows.map((r) => ({
      "Invoice #":      r.invoice_no || "",
      "Order #":        r.orderno || "",
      "Billing Period": r.billing_period || "",
      "Invoice Type":   r.invoice_type === "partial" ? "Partial Invoice" : r.invoice_type === "follow_up" ? "Follow-up Invoice" : "Standard Invoice",
      "Issue Date":     r.invoice_date ? r.invoice_date.slice(0, 10) : "",
      "Due Date":       r.due_date     ? r.due_date.slice(0, 10)     : "",
      "Currency":       r.currency || "USD",
      "Grand Total":    Number(r.grand_total  || 0),
      "Amount Paid":    Number(r.amount_paid  || 0),
      "Balance Due":    Number(r.balance_due  || 0),
      "Payment Status": r.payment_status || "",
      "Payment Terms":  r.payment_terms  || "",
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = [
      { wch: 18 }, { wch: 14 }, { wch: 24 }, { wch: 18 },
      { wch: 12 }, { wch: 12 }, { wch: 8  }, { wch: 12 },
      { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 14 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Invoices");
    XLSX.writeFile(wb, `Invoices_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // ── Render: KPI cards ─────────────────────────────────────────────────────

  renderKpiCards() {
    const { summary, rows } = this.state;
    const overdueCount  = rows.filter(isOverdue).length;
    const unpaidCount   = rows.filter(r => r.payment_status !== "Paid").length;
    const paidCount     = rows.filter(r => r.payment_status === "Paid").length;
    const totalCollected = rows.reduce((sum, r) => sum + (Number(r.amount_paid) || 0), 0);

    const cards = [
      {
        label: "TOTAL INVOICES", value: String(Number(summary.invoice_count) || 0), sub: "All time",
        iconBg: "#ede9fb", iconColor: "#7c3aed", icon: "ni ni-file-docs",
      },
      {
        label: "TOTAL OUTSTANDING", value: fmtCompact(summary.total_outstanding),
        sub: `${unpaidCount} unpaid`, subExtra: overdueCount > 0 ? `· ${overdueCount} overdue` : null,
        iconBg: "#fff0e6", iconColor: "#ea580c", icon: "ni ni-clock",
      },
      {
        label: "TOTAL PAID", value: fmtCompact(totalCollected),
        sub: `${paidCount} paid invoice${paidCount !== 1 ? "s" : ""}`,
        iconBg: "#e6f9f0", iconColor: "#16a34a", icon: "ni ni-check-circle",
      },
    ];

    return (
      <div className="inv-kpi-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
        {cards.map((c, i) => (
          <div className="inv-kpi-card" key={i}>
            <div className="inv-kpi-left">
              <div className="inv-kpi-label">{c.label}</div>
              <div className="inv-kpi-value">{c.value}</div>
              <div className="inv-kpi-sub">
                {c.sub}
                {c.subExtra && <span className="inv-kpi-overdue-chip">{c.subExtra}</span>}
              </div>
            </div>
            <div className="inv-kpi-icon" style={{ background: c.iconBg, color: c.iconColor }}>
              <em className={`icon ${c.icon}`}></em>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ── Render: page header ───────────────────────────────────────────────────

  renderPageHeader() {
    return (
      <div className="inv-page-header">
        <div>
          <div className="inv-page-breadcrumb">
            <span>Billing</span>
            <span className="inv-page-breadcrumb-sep">›</span>
            <span className="inv-page-breadcrumb-current">Invoices</span>
          </div>
          <div className="inv-page-title">Invoices</div>
          <div className="inv-page-subtitle">Your invoice history and payment records</div>
        </div>
      </div>
    );
  }

  // ── Render: filter bar ────────────────────────────────────────────────────

  renderFilters() {
    const { search, statusFilter, orderFilter } = this.state;

    return (
      <>
        {orderFilter && (
          <div className="inv-order-filter-banner">
            <em className="icon ni ni-filter-alt" style={{ marginRight: 6 }}></em>
            Filtering by Order <strong>{orderFilter}</strong>.{" "}
            <span className="inv-order-filter-clear" onClick={this.clearOrderFilter}>
              Show all →
            </span>
          </div>
        )}
        <div className="inv-filter-bar">
          <div className="inv-filter-left">
            <div className="inv-search-wrap">
              <em className="icon ni ni-search inv-search-icon"></em>
              <input
                type="text"
                className="inv-search-input"
                placeholder="Search invoices…"
                value={search}
                onChange={this.onSearchChange}
              />
            </div>
            <select className="inv-filter-select" value={statusFilter} onChange={this.onStatusFilter}>
              <option value="">All Status</option>
              <option value="Paid">Paid</option>
              <option value="Pending">Unpaid</option>
              <option value="Partial">Partial</option>
            </select>
          </div>
          <button
            className="btn btn-secondary"
            onClick={this.exportToExcel}
            disabled={!this.state.rows.length}
          >
            <em className="icon ni ni-download" style={{ marginRight: 5, fontSize: 14 }}></em>
            Export
          </button>
        </div>
      </>
    );
  }

  // ── Render: pagination ────────────────────────────────────────────────────

  renderPagination() {
    const { page, limit, total, loading } = this.state;
    if (total <= 0) return null;
    const totalPages = Math.max(1, Math.ceil(total / limit));
    const start = (page - 1) * limit + 1;
    const end = Math.min(page * limit, total);

    const buildPages = () => {
      const from = Math.max(1, Math.min(page - 2, totalPages - 4));
      const to = Math.min(totalPages, from + 4);
      const pages = [];
      for (let p = from; p <= to; p++) pages.push(p);
      return pages;
    };

    return (
      <div className="inv-pagination-bar">
        <span className="inv-pagination-info">
          Showing {start}–{end} of {total}
        </span>
        <div className="inv-pagination-pages">
          <button
            className="inv-page-btn"
            onClick={() => this.onPageChange(page - 1)}
            disabled={page === 1 || loading}
          >‹</button>
          {buildPages().map((p) => (
            <button
              key={p}
              className={`inv-page-btn${p === page ? " active" : ""}`}
              onClick={() => this.onPageChange(p)}
              disabled={loading}
            >{p}</button>
          ))}
          <button
            className="inv-page-btn"
            onClick={() => this.onPageChange(page + 1)}
            disabled={page === totalPages || loading}
          >›</button>
        </div>
      </div>
    );
  }

  // ── Render: listing table ─────────────────────────────────────────────────

  renderList() {
    const { rows, total, loading } = this.state;
    const d = this._decimals;

    const columns = [
      {
        name: "invoice_no", label: "INVOICE #",
        render: (row) => (
          <span className="inv-table-link" onClick={() => this.openDetail(row)}>
            {row.invoice_no}
          </span>
        ),
      },
      {
        name: "orderno", label: "ORDER #",
        render: (row) => <span className="inv-table-mono">{row.orderno || "—"}</span>,
      },
      {
        name: "billing_period", label: "BILLING PERIOD",
        render: (row) => (
          <span className="inv-table-muted" style={{ whiteSpace: "nowrap" }}>
            {row.billing_period || "—"}
          </span>
        ),
      },
      {
        name: "amount", label: "TOTAL AMOUNT",
        render: (row) => <span className="inv-table-amount">{fmtCurrency(row.grand_total, d)}</span>,
      },
      {
        name: "credit_applied", label: "CREDIT APPLIED",
        render: (row) => {
          const credit = Number(row.credit_applied) || 0;
          return (
            <span style={{ color: credit > 0 ? "#1A7A4A" : "#9CA3AF" }}>
              {credit > 0 ? fmtCurrency(credit, d) : "—"}
            </span>
          );
        },
      },
      {
        name: "paid", label: "Paid Amount",
        render: (row) => {
          const paid = Number(row.amount_paid) || 0;
          return <span style={{ color: paid > 0 ? "#1A7A4A" : "#9CA3AF" }}>{fmtCurrency(paid, d)}</span>;
        },
      },
      {
        name: "balance", label: "BALANCE",
        render: (row) => {
          const balance = getInvoiceBalance(row);
          const settled = balance <= 0;
          return settled
            ? <span style={{ color: "#1A7A4A", fontWeight: 600 }}>✓ Settled</span>
            : <span style={{ color: "#DC2626", fontWeight: 600 }}>{fmtCurrency(balance, d)}</span>;
        },
      },
      {
        name: "status", label: "STATUS",
        render: (row) => <StatusBadge status={row.payment_status} row={row} />,
      },
      {
        name: "actions", label: "ACTIONS",
        render: (row) => (
          <span
            className="inv-inline-view-btn"
            onClick={(e) => { e.stopPropagation(); this.openDetail(row); }}
            title="View Invoice"
          >
            <em className="icon ni ni-eye"></em>
          </span>
        ),
      },
    ];

    const { search, statusFilter, orderFilter, dateFrom, dateTo, filtersApplied } = this.state;
    const showEmptyState = total === 0 && !loading && !filtersApplied;

    return (
      <>
        {this.renderFilters()}
        <div style={{ overflowX: "auto", width: "100%" }}>
          <table className="inv-list-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.name}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {showEmptyState ? (
                <tr>
                  <td colSpan={columns.length} style={{ textAlign: "center", padding: "32px" }}>
                    <div className="inv-empty-state" style={{ margin: 0 }}>
                      <div className="inv-empty-icon">🧾</div>
                      <div className="inv-empty-title">No invoices yet</div>
                      <div className="inv-empty-sub">Your invoices will appear here once they are issued.</div>
                    </div>
                  </td>
                </tr>
              ) : loading ? (
                <tr>
                  <td colSpan={columns.length} style={{ textAlign: "center", padding: "32px", color: "#6B6762" }}>
                    <span className="spinner-border spinner-border-sm" />
                    <span style={{ marginLeft: "8px" }}>Loading…</span>
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} style={{ textAlign: "center", padding: "32px", color: "#9A9690" }}>
                    No invoices match your filters.
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => (
                  <tr
                    key={idx}
                    style={{ cursor: "pointer" }}
                    onClick={() => this.openDetail(row)}
                  >
                    {columns.map((col) => (
                      <td key={col.name}>
                        {typeof col.render === "function"
                          ? col.render(row)
                          : row[col.name] != null ? String(row[col.name]) : ""}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {!loading && rows.length > 0 && this.renderPagination()}
      </>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────

  render() {
    console.log("[Invoices] render → state:", this.state, "defaultSettings:", this.props.defaultSettings, "is_msa_signed:", this.props.is_msa_signed);
    return (
      <div className="nk-content">
        <div className="container-fluid">
          {this.props.defaultSettings === "missing" ? <SettingsError /> : null}
          {this.props.is_msa_signed === "No" ? <MsaWarning /> : null}
          <div className="nk-content-inner">
            <div className="nk-content-body">
              <div className="card card-bordered rem-invoice-wrap">
                <div className="card-inner">
                  {this.renderPageHeader()}
                  {this.renderKpiCards()}
                  {this.renderList()}
                </div>
              </div>
            </div>
          </div>
        </div>

        <ViewInvoiceModal
          show={this.state.showViewModal}
          invoiceNo={this.state.selectedInvoice && this.state.selectedInvoice.invoice_no}
          onClose={this.closeViewModal}
        />
        <NotificationToast
          message={this.state.toastMsg}
          type={this.state.toastType}
          onDismiss={() => this.setState({ toastMsg: "" })}
        />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  defaultSettings: state.settings,
  is_msa_signed: state.is_msa_signed,
  quotationSettings: state.quotationSettings,
});
const mapDispatchToProps = (dispatch) => ({
  addSettings: (data) => dispatch(addSettings(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Invoices));
